import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PHONE, PHONE_HREF, EMAIL, SERVICE_AREAS } from "@/lib/site";
import { Loader2, Send, Phone, Mail, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "h-12 rounded-none bg-black border border-[#222] text-white placeholder:text-[#666] focus-visible:ring-1 focus-visible:ring-[#FF4D00] focus-visible:ring-offset-0 focus-visible:border-[#FF4D00]";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get right back to you.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Failed to send. Call us at " + PHONE);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative z-10 border-t border-[#222] bg-[#0a0a0a] px-4 py-20 md:px-12 lg:px-24 md:py-28">
      <div className="mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono-a text-xs tracking-[0.2em] uppercase text-[#FF4D00]">/ Contact Us</p>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-4 leading-[0.95]">
            Got Junk?<br />Talk To The Guys
          </h2>
          <p className="text-[#888] text-base md:text-lg mt-5 max-w-md">
            Call, email, or drop us a line below. A real member of the family answers — no bots, no call center.
          </p>
          <div className="mt-10 space-y-6">
            <a href={PHONE_HREF} data-testid="contact-phone-link" className="flex items-center gap-4 group">
              <span className="flex h-12 w-12 items-center justify-center border border-[#222] group-hover:border-[#FF4D00] group-hover:bg-[#FF4D00] transition-colors">
                <Phone className="h-5 w-5 text-[#FF4D00] group-hover:text-black transition-colors" />
              </span>
              <div>
                <p className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Call / Text</p>
                <p className="text-white text-lg font-semibold">{PHONE}</p>
              </div>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-4 group">
              <span className="flex h-12 w-12 items-center justify-center border border-[#222] group-hover:border-[#FF4D00] group-hover:bg-[#FF4D00] transition-colors">
                <Mail className="h-5 w-5 text-[#FF4D00] group-hover:text-black transition-colors" />
              </span>
              <div>
                <p className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Email</p>
                <p className="text-white text-lg font-semibold break-all">{EMAIL}</p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 items-center justify-center border border-[#222]">
                <MapPin className="h-5 w-5 text-[#FF4D00]" />
              </span>
              <div>
                <p className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Serving</p>
                <p className="text-white text-sm">{SERVICE_AREAS.join(" · ")}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="border border-[#222] bg-[#0d0d0d] p-6 md:p-8 space-y-5"
        >
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Name *</Label>
            <Input data-testid="contact-name" className={field} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Phone *</Label>
              <Input data-testid="contact-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="(720) 000-0000" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Email *</Label>
              <Input data-testid="contact-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono-a text-[11px] tracking-widest uppercase text-[#888]">Message *</Label>
            <Textarea data-testid="contact-message" className={`${field} h-32 py-3`} value={form.message} onChange={set("message")} placeholder="Tell us what you need hauled away..." />
          </div>
          <button
            data-testid="contact-submit" type="submit" disabled={loading}
            className="w-full h-12 bg-[#FF4D00] hover:bg-[#FF6A00] text-black font-mono-a uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? "Sending..." : "Send Message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
